import { useEffect, useState } from "react";
import axios from "axios";
import { loadAttribution } from "../../lib/retailAttribution";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;
const SESSION_KEY = "retail_session_id";

// Session-binding security correction (Aug 2026, PR #121 follow-up) —
// this id is what `/otp/issue`'s verified-grant reuse check and the
// order-placement phone-verification gate both use as the MANDATORY
// proof that a reuse request comes from the SAME browser session an
// earlier OTP was verified in. The previous generator
// (`Math.random().toString(36) + Date.now().toString(36)`) is not a
// cryptographically secure random source — `Math.random()` is not
// specified to be unpredictable, and `Date.now()` is trivially
// observable, together giving an attacker a far smaller search space
// than a real secret should. `crypto.randomUUID()` (122 bits of CSPRNG
// entropy) is the narrowest available upgrade: same call site, same
// stored/compared-as-an-opaque-string usage, no new dependency, no
// change to how the backend validates it. `crypto.getRandomValues` is
// the fallback for the rare browser with Web Crypto but no
// `randomUUID`; the legacy generator is a last resort only for a
// non-secure context or an ancient browser lacking Web Crypto
// entirely — session identity there is still exact-match required
// server-side, so a weaker id only ever costs THIS tab reuse
// convenience, never a security guarantee.
function generateSecureSessionId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `r-${crypto.randomUUID()}`;
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(18));
      return `r-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
    }
  } catch (_e) {
    // fall through to the legacy generator below
  }
  return `r-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

// Phase 3M / Iter43 (Feb 2026) — extracted verbatim from RetailCustomer.jsx.
// `catalogId` was added in Iter43 P0 to fix the `product_not_found`
// regression on non-default storefronts: without it the backend would
// fall back to `pick_active_catalog(catalog_id=None)` (the resolver
// winner) and reject products from any other catalog. We now thread the
// link-resolved catalog id through every cart op so the cart binds to
// the SAME catalog the customer is browsing.
//
// Behaviour:
// - one session id per browser tab (rotates only via startNewOrder())
// - cart fetched once per (slug, sessionId) tuple
// - 404 on initial fetch → empty cart silently (no toast)
// - any cartOp failure surfaces a toast and rethrows for caller awareness
export default function useRetailCart({ slug, gateError, catalogId }) {
  const [sessionId] = useState(() => {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = generateSecureSessionId();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  });
  const [cart, setCart] = useState(null);
  // Gate C5A-3A — items from a cart that `checkout()` already spent,
  // whose draft order was never paid. NOT the live cart: it is not
  // editable and must not be rendered as though it were, or the
  // customer edits something that no longer exists. It exists so the
  // storefront can offer "Return to cart" instead of showing an empty
  // shop after an address, quote or provider failure.
  const [recoverableCart, setRecoverableCart] = useState(null);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    if (gateError) return;
    axios.get(`${RETAIL_BASE}/${slug}/cart/${sessionId}`)
      .then((r) => {
        if (r?.data?.recoverable) {
          setCart(null);
          setRecoverableCart(r.data.cart || null);
        } else {
          setCart(r.data.cart);
          setRecoverableCart(null);
        }
      })
      .catch((e) => {
        // 404 = no open cart for this session yet (expected after a fresh
        // "Start New Order" or first visit). Treat as empty cart silently.
        if (e?.response?.status === 404) setCart(null);
        else setCart(null);
        setRecoverableCart(null);
      });
  }, [slug, sessionId, gateError]);

  const cartOp = async (op, extra = {}) => {
    try {
      // Always thread the active catalog id so the backend binds the
      // cart to the SAME catalog the storefront is rendering. Without
      // this, /retail/{tenant}/{link_slug} customers on a non-resolver-
      // winning storefront get `product_not_found` on every Add.
      const body = { op, session_id: sessionId, ...extra };
      if (catalogId && body.catalog_id === undefined) {
        body.catalog_id = catalogId;
      }
      // Marketing P0-4 — this storefront's campaign touches (slug-scoped).
      // Optional; the server ignores it unless attribution is enabled.
      const touches = loadAttribution(slug);
      if (touches) body.attribution_touch = touches;
      const { data } = await axios.post(`${RETAIL_BASE}/${slug}/cart`, body);
      setCart(data.cart);
      return data.cart;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong";
      toast.error(typeof detail === "string" ? detail : "Something went wrong");
      throw e;
    }
  };

  // Gate C5A-3A — the explicit customer action. Never automatic: the
  // customer decides whether to pick their order back up, and one press
  // performs one recovery. The server upserts on the same
  // (company, session, open) tuple every other cart lookup uses, so a
  // double-tap converges on one cart rather than creating two.
  const recoverCart = async () => {
    if (recovering) return null;
    setRecovering(true);
    try {
      const { data } = await axios.post(`${RETAIL_BASE}/${slug}/cart`, {
        op: "recover", session_id: sessionId,
      });
      setCart(data.cart);
      setRecoverableCart(null);
      return data.cart;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        detail === "cart_not_recoverable"
          ? "Those items are no longer available to restore. Please start a new order."
          : "Could not restore your items. Please try again.",
      );
      return null;
    } finally {
      setRecovering(false);
    }
  };

  // Customer-selected spice level (Sep 2026, restored) — sent with the
  // add, never stored in component state: the SERVER owns the cart line
  // and re-derives line identity `(product_id, spice_level)` itself, so
  // the same dish at two heats is two lines no matter what the client
  // believes. `null` for a product with no selector, which is
  // byte-identical to what this sent before.
  const addProduct = (productId, spiceLevel = null) =>
    cartOp("add", {
      product_id: productId, quantity: 1,
      ...(spiceLevel ? { spice_level: spiceLevel } : {}),
    });
  const setQty     = (itemId, qty) => cartOp("set_qty", { item_id: itemId, quantity: qty });
  const removeItem = (itemId) => cartOp("remove", { item_id: itemId });
  const clearCart  = () => cartOp("clear");
  // Item-level Takeout marker (Sep 2026) — informational only, and
  // persisted SERVER-side on the cart line (`retail/cart.py::
  // set_item_takeout`) rather than held in this component's state, so
  // it survives a refresh and is already part of the authoritative item
  // snapshot by the time checkout copies the cart into the order. Uses
  // the line's existing `item_id`, the same identity `set_qty` and
  // `remove` address.
  const setItemTakeout = (itemId, takeout) =>
    cartOp("set_item_takeout", { item_id: itemId, item_takeout: !!takeout });

  return {
    sessionId, cart, setCart, addProduct, setQty, removeItem, clearCart,
    setItemTakeout,
    recoverableCart, recoverCart, recovering,
  };
}
